import { useLocalizedPath } from "../i18n/useLocalizedPath";
import React from "react";
import {
  Box,
  Heading,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Text,
  Link,
  Container,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

type FAQItem = {
  question: string;
  answer: string;
  href?: string;
  linkText?: string;
};

const FAQ: React.FC = () => {
  const { t } = useTranslation();
  const localizePath = useLocalizedPath();

  // FAQ data structure
  const faqItems: FAQItem[] = [
    {
      question: "faq.privacy.question",
      answer: "faq.privacy.answer",
    },
    {
      question: "faq.usage.question",
      answer: "faq.usage.answer",
    },
    {
      question: "faq.gettingStarted.question",
      answer: "faq.gettingStarted.answer",
    },
    {
      question: "faq.media.question",
      answer: "faq.media.answer",
    },
    {
      question: "faq.aiWriting.question",
      answer: "faq.aiWriting.answer",
    },
    {
      question: "faq.sharing.question",
      answer: "faq.sharing.answer",
    },
    {
      question: "faq.hexaco.question",
      answer: "faq.hexaco.answer",
      href: "/hexaco",
      linkText: "faq.hexaco.link",
    },
    {
      question: "faq.devices.question",
      answer: "faq.devices.answer",
    },
    {
      question: "faq.data.question",
      answer: "faq.data.answer",
    },
  ];

  return (
    <Box 
      as="section" 
      py={{ base: 12, md: 16 }}
      id="faq"
      scrollMarginTop="110px"
    >
      <Container maxW="container.md">
        <VStack spacing={8} align="stretch">
          <Heading 
            as="h2" 
            fontSize={{ base: "2xl", md: "3xl" }}
            textAlign="center"
            mb={6}
          >
            {t("faq.title")}
          </Heading>
          
          <Text textAlign="center" mb={8}>
            {t("faq.subtitle")}
          </Text>
          
          <Accordion allowMultiple>
            {faqItems.map((item, index) => (
              <AccordionItem 
                key={index} 
                mb={4} 
                border="1px solid" 
                borderColor="gray.200" 
                borderRadius="md"
              >
                <h3>
                  <AccordionButton 
                    py={4} 
                    _hover={{ bg: "gray.50" }}
                  >
                    <Box flex="1" textAlign="left" fontWeight="600">
                      {t(item.question)}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h3>
                <AccordionPanel pb={4}>
                  <Text whiteSpace="pre-line" lineHeight="1.8">
                    {t(item.answer)}
                  </Text>
                  {item.href && item.linkText && (
                    <Link
                      as={RouterLink}
                      to={localizePath(item.href)}
                      display="inline-block"
                      mt={3}
                      color="primary.500"
                      fontWeight="600"
                      textDecoration="underline"
                      textUnderlineOffset="4px"
                      _hover={{ color: "primary.600" }}
                      _focusVisible={{
                        outline: "3px solid",
                        outlineColor: "primary.300",
                        outlineOffset: "3px",
                      }}
                    >
                      {t(item.linkText)}
                    </Link>
                  )}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </VStack>
      </Container>
    </Box>
  );
};

export default FAQ;

